// Joukkuekausi (Teams / sarjaottelut). See docs/features/teams.md §4.1.
//
// A season IS the availability calendar: its date range is the window teams may book into,
// its resources are the courts the league may touch, and its slots are the times. It lives in
// the `resourcepricings` collection under type 'teamspricing', alongside resourcepricing /
// membershippricing / giftcodepricing / recurringsalepricing, so it reuses the whole calendar
// + FalconModal + rjsf editor machinery.
//
// Deliberately NOT copied from _resourcePricingClass:
//   - priority          a season has no overlap to resolve: a team's seasonid names its
//                       calendar outright, so two seasons may overlap in dates freely.
//   - nonlinearpricing  package pricing is a shop concept; league play is one length per
//     packagebandmode   format row and the row decides it.

export const _teamSeasonSlotClass = {
  displayName: { type: 'string', title: 'Vuorojen kuvaus' },
  days: {
    type: 'array',
    title: 'Päivät',
    items: {
      type: 'number',
      oneOf: [
        { const: 1, title: 'Maanantai' },
        { const: 2, title: 'Tiistai' },
        { const: 3, title: 'Keskiviikko' },
        { const: 4, title: 'Torstai' },
        { const: 5, title: 'Perjantai' },
        { const: 6, title: 'Lauantai' },
        { const: 7, title: 'Sunnuntai' },
      ],
    },
    uniqueItems: true,
  },
  starttime: { type: 'string', title: 'Alkuaika' },
  endtime: { type: 'string', title: 'Loppuaika' },
  // Narrows the season's own resource list for this slot only. Empty = inherit every court
  // on the season. This is the one structural difference from _resourcePricingSlotClass and
  // it exists because a season is a single document: without it the model cannot say
  // "Mon 18-21 on courts 1-3, Sat 09-12 on courts 4-6". Filled at runtime by the container.
  resources: {
    type: 'array',
    title: 'Kentät (tyhjä = kaikki kauden kentät)',
    items: { type: 'string', oneOf: [] },
    uniqueItems: true,
  },
  price: { type: 'number', title: 'Hinta' },
  pricingunit: {
    type: 'string',
    title: 'Hinnan yksikkö',
    oneOf: [{ const: 'h', title: 'tunti' }],
  },
}

export const _teamSeasonSlotUISchema = {
  days: { 'ui:widget': 'checkboxes' },
  starttime: { 'ui:widget': 'timeWidget' },
  endtime: { 'ui:widget': 'timeWidget' },
  resources: {
    'ui:widget': 'checkboxes',
    'ui:help': 'Jätä tyhjäksi jos vuoro koskee kaikkia kauden kenttiä.',
  },
}

export const _teamSeasonClass = {
  name: { type: 'string', title: 'Kauden nimi' },
  active: { type: 'boolean', title: 'Active' },
  validstart: { type: 'string', format: 'date', title: 'Kausi alkaa' },
  validend: { type: 'string', format: 'date', title: 'Kausi päättyy' },
  // Rolling booking horizon: the last bookable date is min(validend, today + bookingdaysahead).
  // Empty / 0 = no limit, validend alone decides.
  bookingdaysahead: { type: 'integer', minimum: 0, title: 'Varattavissa enintään (päivää etukäteen)' },
  bookingopens: { type: 'string', format: 'date', title: 'Varaaminen alkaa' },
  bookingcloses: { type: 'string', format: 'date', title: 'Varaaminen päättyy' },
  // Every court the league may touch. A slot may narrow this; it may never widen it.
  resources: {
    type: 'array',
    title: 'Kentät',
    items: { type: 'string', oneOf: [] },
    uniqueItems: true,
  },
  // Which formats a team on this season may book. Filled at runtime from teamformats.
  formats: {
    type: 'array',
    title: 'Sallitut ottelumuodot',
    items: { type: 'string', oneOf: [] },
    uniqueItems: true,
  },
  payerdefault: {
    type: 'string',
    title: 'Kuka maksaa varaukset',
    default: 'team',
    oneOf: [
      { const: 'team', title: 'Joukkue (joukkueelle merkitty maksaja)' },
      { const: 'manager', title: 'Joukkueenjohtaja' },
      { const: 'organisation', title: 'Kiinteä yritys / seura' },
    ],
  },
}

export const _teamSeasonClassDeps = {
  payerdefault: {
    oneOf: [
      {
        properties: {
          payerdefault: { enum: ['team'] },
        },
      },
      {
        properties: {
          payerdefault: { enum: ['manager'] },
        },
      },
      {
        properties: {
          payerdefault: { enum: ['organisation'] },
          // The organisation's document id (customers/{id}/organisations). Picked by search
          // through organisationWidget; the container supplies the list via ui:options.
          payerorganisationid: { type: 'string', title: 'Yritys / seura' },
        },
      },
    ],
  },
}

export const _teamSeasonUISchema = {
  bookingdaysahead: {
    'ui:help': 'Viimeinen varattava päivä on kauden loppu tai tämä päivä + näin monta päivää, kumpi tulee ensin. Tyhjä tai 0 = ei rajaa.',
  },
  bookingopens: {
    'ui:help': 'Ennen tätä päivää joukkueenjohtajat eivät voi varata.',
  },
  bookingcloses: {
    'ui:help': 'Tämän jälkeen joukkueenjohtaja voi enää perua, ei varata.',
  },
  resources: { 'ui:widget': 'checkboxes' },
  formats: { 'ui:widget': 'checkboxes' },
  payerorganisationid: {
    'ui:widget': 'organisationWidget',
    'ui:help': 'Hae nimellä, Y-tunnuksella tai sähköpostilla.',
  },
}
